import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  Mail,
  RefreshCcw,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { fetchBlobWithAuth, fetchWithAuth } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import {
  documentKeys,
  isInFlight,
  uploadDocument,
  useDeleteDocument,
  useDocumentDetail,
  useDocuments,
  useDocumentsStatus,
  useReprocessDocument,
  type DocumentEstado,
  type DocumentOut,
  type SourceKind,
} from "@/hooks/use-documents";
import { createSemaphore } from "@/lib/semaphore";
import { showToast } from "@/lib/toast";
import { formatDate, formatFileSize } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Pagination } from "@/components/ui/pagination";
import { DocumentDropzone } from "./document-dropzone";
import {
  DocumentUploadQueue,
  type QueueItem,
  type UploadState,
} from "./document-upload-queue";
import { DocumentTextViewer } from "./document-text-viewer";

// ---------------------------------------------------------------------------
// Etiquetas / estilos por estado y tipo
// ---------------------------------------------------------------------------

// Pills Cuerva (esquina recta): neutro/en-proceso (amarillo pálido)/operativo
// (negro)/error (rojo contenido)/inactivo (gris).
const estadoColors: Record<DocumentEstado, string> = {
  PENDIENTE: "bg-surface-300 text-content",
  EXTRAYENDO_TEXTO: "bg-energia-pale text-ink",
  GENERANDO_CHUNKS: "bg-energia-pale text-ink",
  GENERANDO_CONTEXTO: "bg-energia-pale text-ink",
  GENERANDO_EMBEDDINGS: "bg-energia-pale text-ink",
  COMPLETADO: "bg-brand text-white",
  ERROR: "bg-danger/10 text-danger",
  DUPLICADO: "bg-line text-content-muted",
};

const estadoLabels: Record<DocumentEstado, string> = {
  PENDIENTE: "En cola",
  EXTRAYENDO_TEXTO: "Extrayendo texto…",
  GENERANDO_CHUNKS: "Generando chunks…",
  GENERANDO_CONTEXTO: "Generando contexto…",
  GENERANDO_EMBEDDINGS: "Generando embeddings…",
  COMPLETADO: "Completado",
  ERROR: "Error",
  DUPLICADO: "Duplicado",
};

const tipoLabels: Record<SourceKind, string> = {
  pdf: "PDF",
  docx: "Word",
  xlsx: "Excel",
  email_eml: "Email",
  email_msg: "Email",
  email_txt: "Email",
  txt: "Texto",
  attachment: "Adjunto",
};

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Sección Documentos: dropzone + cola de subida + tabla con polling batched
// ---------------------------------------------------------------------------

interface DocumentSectionProps {
  projectId: string;
}

export function DocumentSection({ projectId }: DocumentSectionProps) {
  const queryClient = useQueryClient();

  // ---- filtros / paginación ----
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const q = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    setPage(1);
  }, [q, estadoFilter]);

  const documentsQuery = useDocuments(projectId, {
    q: q || undefined,
    estado: estadoFilter || undefined,
    page,
    size: PAGE_SIZE,
  });
  const docs = useMemo(
    () => documentsQuery.data?.items ?? [],
    [documentsQuery.data]
  );
  const total = documentsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [...documentKeys.all, "list", projectId],
    });
  }, [queryClient, projectId]);

  // ---- cola de subida (concurrencia 4 vía semáforo, 1 fichero por POST) ----
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const limitRef = useRef(createSemaphore(4));

  const patchQueueItem = useCallback(
    (key: string, patch: Partial<QueueItem>) => {
      setQueue((prev) =>
        prev.map((it) => (it.key === key ? { ...it, ...patch } : it))
      );
    },
    []
  );

  const uploadOne = useCallback(
    async (item: QueueItem) => {
      patchQueueItem(item.key, { state: "subiendo", error: undefined });
      try {
        const doc = await uploadDocument(projectId, item.file);
        let state: UploadState = "procesando";
        if (doc.estado === "DUPLICADO") state = "duplicado";
        else if (doc.estado === "ERROR") state = "error";
        else if (doc.estado === "COMPLETADO") state = "completado";
        patchQueueItem(item.key, { state, documentId: doc.id });
        invalidateList();
      } catch (error) {
        patchQueueItem(item.key, {
          state: "error",
          error:
            error instanceof Error ? error.message : "Error al subir el archivo",
        });
      }
    },
    [projectId, patchQueueItem, invalidateList]
  );

  const enqueueFiles = useCallback(
    (files: File[]) => {
      const items: QueueItem[] = files.map((file) => ({
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        state: "en_cola",
      }));
      setQueue((prev) => [...prev, ...items]);
      for (const item of items) {
        void limitRef.current(() => uploadOne(item));
      }
    },
    [uploadOne]
  );

  // Side-effect fuera del updater de setState: en StrictMode los updaters
  // pueden ejecutarse dos veces y dispararían la subida duplicada.
  const retryItem = useCallback(
    (key: string) => {
      const item = queue.find((it) => it.key === key);
      if (!item) return;
      const reset: QueueItem = { ...item, state: "en_cola", error: undefined };
      setQueue((prev) => prev.map((it) => (it.key === key ? reset : it)));
      void limitRef.current(() => uploadOne(reset));
    },
    [queue, uploadOne]
  );

  // ---- polling BATCHED cada 2s mientras haya docs en vuelo ----
  const inFlightIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of queue) {
      if (it.documentId && it.state === "procesando") ids.add(it.documentId);
    }
    for (const d of docs) {
      if (isInFlight(d.estado)) ids.add(d.id);
    }
    return Array.from(ids).sort();
  }, [queue, docs]);

  const statusQuery = useDocumentsStatus(projectId, inFlightIds);
  const terminalSeenRef = useRef<Set<string>>(new Set());

  // Estado terminal por documentId, fusionando dos fuentes de verdad:
  //  1) el polling batched (statusQuery) — el que cambia primero, y
  //  2) la tabla de documentos (docs) — fiable y siempre poblada tras el
  //     invalidate.
  // El bug original solo miraba (1): si el documento terminaba y el set de
  // ids del polling se recomputaba (gcTime: 0 descarta los datos), el ítem
  // de la cola se quedaba clavado en "Procesando…" y el contador "X/Y" no
  // avanzaba aunque la tabla ya mostrase "Completado". Mirar también (2) lo
  // resuelve de forma robusta.
  const terminalEstadoById = useMemo(() => {
    const map = new Map<
      string,
      { estado: DocumentEstado; processing_error: string | null }
    >();
    for (const s of statusQuery.data ?? []) {
      if (!isInFlight(s.estado)) {
        map.set(s.id, {
          estado: s.estado,
          processing_error: s.processing_error,
        });
      }
    }
    for (const d of docs) {
      if (!isInFlight(d.estado) && !map.has(d.id)) {
        map.set(d.id, {
          estado: d.estado,
          processing_error: d.processing_error,
        });
      }
    }
    return map;
  }, [statusQuery.data, docs]);

  useEffect(() => {
    if (terminalEstadoById.size === 0) return;

    // Transiciones de la cola: procesando → completado/error/duplicado.
    setQueue((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (!item.documentId || item.state !== "procesando") return item;
        const st = terminalEstadoById.get(item.documentId);
        if (!st) return item;
        changed = true;
        if (st.estado === "ERROR")
          return {
            ...item,
            state: "error" as const,
            error: st.processing_error ?? "Error de procesamiento",
          };
        if (st.estado === "DUPLICADO")
          return { ...item, state: "duplicado" as const };
        return { ...item, state: "completado" as const };
      });
      return changed ? next : prev;
    });

    // Refrescar la lista solo cuando algo acaba (no en cada tick).
    const newlyTerminal = Array.from(terminalEstadoById.keys()).filter(
      (id) => !terminalSeenRef.current.has(id)
    );
    if (newlyTerminal.length > 0) {
      for (const id of newlyTerminal) terminalSeenRef.current.add(id);
      invalidateList();
    }
  }, [terminalEstadoById, invalidateList]);

  // Overrides en vivo para las filas de la tabla.
  const statusById = useMemo(() => {
    const map = new Map<string, { estado: DocumentEstado; progress: number }>();
    for (const s of statusQuery.data ?? []) {
      map.set(s.id, { estado: s.estado, progress: s.progress });
    }
    return map;
  }, [statusQuery.data]);

  // ---- acciones por documento ----
  const deleteMutation = useDeleteDocument(projectId);
  const reprocessMutation = useReprocessDocument(projectId);
  const [pendingDelete, setPendingDelete] = useState<DocumentOut | null>(null);
  const [viewerDoc, setViewerDoc] = useState<DocumentOut | null>(null);
  const detailQuery = useDocumentDetail(viewerDoc?.id ?? "", !!viewerDoc);

  // ---- selección y acciones en lote ----
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Limpiar selección al cambiar de página/filtro/búsqueda.
  useEffect(() => {
    setSelected(new Set());
  }, [q, estadoFilter, page]);

  const liveEstado = useCallback(
    (doc: DocumentOut) => statusById.get(doc.id)?.estado ?? doc.estado,
    [statusById]
  );
  const errorDocs = useMemo(
    () => docs.filter((d) => liveEstado(d) === "ERROR"),
    [docs, liveEstado]
  );

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === docs.length ? new Set() : new Set(docs.map((d) => d.id))
    );
  }, [docs]);

  const bulkReprocess = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0 || bulkBusy) return;
      setBulkBusy(true);
      try {
        await Promise.allSettled(
          ids.map((id) =>
            fetchWithAuth(`/api/v1/documents/${id}/reprocess`, { method: "POST" })
          )
        );
        showToast.success(`${ids.length} documento(s) en reproceso`);
        setSelected(new Set());
        invalidateList();
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkBusy, invalidateList]
  );

  const bulkDelete = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0 || bulkBusy) return;
      setBulkBusy(true);
      try {
        await Promise.allSettled(
          ids.map((id) =>
            fetchWithAuth(`/api/v1/documents/${id}`, { method: "DELETE" })
          )
        );
        showToast.success(`${ids.length} documento(s) eliminado(s)`);
        setSelected(new Set());
        invalidateList();
      } finally {
        setBulkBusy(false);
      }
    },
    [bulkBusy, invalidateList]
  );

  const handleDownload = useCallback(async (doc: DocumentOut) => {
    try {
      const blob = await fetchBlobWithAuth(
        `/api/v1/documents/${doc.id}/download`
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast.error("Error al descargar el documento");
    }
  }, []);

  // Abre el ORIGINAL en una pestaña nueva (PDF se ve con planos/planning).
  // Abrimos la ventana de forma síncrona (gesto del usuario) para evitar el
  // bloqueo de pop-ups, y le fijamos la URL del blob tras la descarga.
  const handleOpenOriginal = useCallback(async (doc: DocumentOut) => {
    const win = window.open("", "_blank");
    try {
      const blob = await fetchBlobWithAuth(
        `/api/v1/documents/${doc.id}/download`
      );
      const url = window.URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, "_blank");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      showToast.error("Error al abrir el documento");
    }
  }, []);

  const hasEmailDocs = docs.some((d) => d.email_meta);

  return (
    <section className="space-y-4">
      <DocumentDropzone onFiles={enqueueFiles} />

      <DocumentUploadQueue
        items={queue}
        onRetry={retryItem}
        onClear={() => setQueue([])}
      />

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-content-muted" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por nombre…"
            className="w-full pl-8 pr-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
          />
        </div>
        <select
          value={estadoFilter}
          onChange={(e) => setEstadoFilter(e.target.value)}
          className="px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand"
          aria-label="Filtrar por estado"
        >
          <option value="">Todos los estados</option>
          {(Object.keys(estadoLabels) as DocumentEstado[]).map((estado) => (
            <option key={estado} value={estado}>
              {estadoLabels[estado]}
            </option>
          ))}
        </select>
        {errorDocs.length > 0 && (
          <Button
            variant="outline"
            disabled={bulkBusy}
            onClick={() => bulkReprocess(errorDocs.map((d) => d.id))}
          >
            {bulkBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4" />
            )}
            Reprocesar erróneos ({errorDocs.length})
          </Button>
        )}
      </div>

      {/* Barra de selección en lote */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-energia-pale border border-line rounded-md px-3 py-2">
          <span className="text-sm text-ink font-medium">
            {selected.size} seleccionado{selected.size === 1 ? "" : "s"}
          </span>
          <div className="flex-1" />
          <Button
            variant="outline"
            disabled={bulkBusy}
            onClick={() => bulkReprocess([...selected])}
          >
            <RefreshCcw className="w-4 h-4" />
            Reprocesar
          </Button>
          <Button
            variant="outline"
            className="text-danger hover:text-danger"
            disabled={bulkBusy}
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-4 h-4" />
            Eliminar
          </Button>
        </div>
      )}

      {/* Tabla */}
      {documentsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-content-muted animate-spin" />
        </div>
      ) : documentsQuery.isError ? (
        <div className="bg-danger/10 border border-danger/30 rounded-md p-3 text-center text-sm text-danger">
          Error cargando los documentos.
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-line-strong rounded-md">
          <FileText className="w-8 h-8 text-content-muted/50 mx-auto mb-2" />
          <p className="text-sm text-content font-medium">
            Aún no hay documentos en este proyecto
          </p>
          <p className="text-xs text-content-muted mt-0.5">
            Sube ficheros para poder consultarlos desde el chat.
          </p>
        </div>
      ) : (
        <div className="bg-surface rounded-lg border border-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-line">
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todos"
                      className="align-middle accent-brand"
                      checked={docs.length > 0 && selected.size === docs.length}
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            selected.size > 0 && selected.size < docs.length;
                      }}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase">
                    Nombre
                  </th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden md:table-cell">
                    Tipo
                  </th>
                  {hasEmailDocs && (
                    <>
                      <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">
                        De
                      </th>
                      <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">
                        Fecha email
                      </th>
                      <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">
                        Asunto
                      </th>
                    </>
                  )}
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase">
                    Estado
                  </th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">
                    Fecha
                  </th>
                  <th className="px-4 py-2 w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {docs.map((doc) => {
                  const live = statusById.get(doc.id);
                  const estado = live?.estado ?? doc.estado;
                  // progress es fracción 0-1 → ×100 para mostrar.
                  const progressPct = Math.round(
                    (live?.progress ?? doc.progress ?? 0) * 100
                  );
                  const processing = isInFlight(estado);
                  const isEmail = !!doc.email_meta;

                  return (
                    <tr key={doc.id} className="hover:bg-surface-100">
                      <td className="px-4 py-2.5 w-10">
                        <input
                          type="checkbox"
                          className="align-middle accent-brand"
                          aria-label={`Seleccionar ${doc.filename}`}
                          checked={selected.has(doc.id)}
                          onChange={() => toggleOne(doc.id)}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {isEmail ? (
                            <Mail className="w-4 h-4 text-content-muted/70 flex-shrink-0" />
                          ) : (
                            <FileText className="w-4 h-4 text-content-muted/70 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <span
                              className="text-sm font-medium text-ink truncate block max-w-[28ch]"
                              title={doc.filename}
                            >
                              {doc.filename}
                            </span>
                            <span className="text-[11px] text-content-muted">
                              {formatFileSize(doc.size_bytes)}
                              {doc.num_chunks != null &&
                                estado === "COMPLETADO" && (
                                  <> · {doc.num_chunks} chunks</>
                                )}
                            </span>
                            {estado === "ERROR" && doc.processing_error && (
                              <p
                                className="mt-0.5 text-xs text-danger break-words max-w-[40ch]"
                                title={doc.processing_error}
                              >
                                {doc.processing_error.length > 140
                                  ? `${doc.processing_error.slice(0, 140)}…`
                                  : doc.processing_error}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-surface-200 text-content">
                          {doc.source_kind
                            ? (tipoLabels[doc.source_kind] ?? doc.source_kind)
                            : "—"}
                        </span>
                      </td>
                      {hasEmailDocs && (
                        <>
                          <td className="px-4 py-2.5 text-xs text-content hidden lg:table-cell max-w-[18ch] truncate">
                            {doc.email_meta?.sender_email ?? "—"}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-content hidden lg:table-cell whitespace-nowrap">
                            {doc.email_meta?.date
                              ? formatDate(doc.email_meta.date)
                              : "—"}
                          </td>
                          <td
                            className="px-4 py-2.5 text-xs text-content hidden lg:table-cell max-w-[24ch] truncate"
                            title={doc.email_meta?.subject ?? undefined}
                          >
                            {doc.email_meta?.subject ?? "—"}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {estado === "COMPLETADO" && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                          )}
                          {estado === "ERROR" && (
                            <XCircle className="w-3.5 h-3.5 text-danger" />
                          )}
                          {estado === "DUPLICADO" && (
                            <Copy className="w-3.5 h-3.5 text-content-muted" />
                          )}
                          {processing && (
                            <Loader2 className="w-3.5 h-3.5 text-brand animate-spin" />
                          )}
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                              estadoColors[estado] ??
                              "bg-surface-200 text-content"
                            }`}
                          >
                            {estadoLabels[estado] ?? estado}
                          </span>
                        </div>
                        {processing && (
                          <div className="mt-1.5 w-full max-w-[140px]">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[10px] text-content-muted">
                                {progressPct}%
                              </span>
                            </div>
                            <div className="w-full bg-surface-200 rounded-full h-1.5">
                              <div
                                className="bg-brand h-1.5 rounded-full transition-all duration-500 ease-out"
                                style={{
                                  width: `${Math.max(progressPct, 5)}%`,
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-content hidden lg:table-cell whitespace-nowrap">
                        {formatDate(doc.created_at)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => setViewerDoc(doc)}
                            className="p-1.5 text-content-muted hover:text-ink rounded transition-colors"
                            title="Ver texto extraído"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenOriginal(doc)}
                            className="p-1.5 text-content-muted hover:text-ink rounded transition-colors"
                            title="Ver original (PDF, con planos/planning)"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1.5 text-content-muted hover:text-ink rounded transition-colors"
                            title="Descargar"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => reprocessMutation.mutate(doc.id)}
                            disabled={processing}
                            className="p-1.5 text-content-muted hover:text-ink rounded transition-colors disabled:opacity-40"
                            title="Reprocesar"
                          >
                            <RefreshCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setPendingDelete(doc)}
                            className="p-1.5 text-content-muted hover:text-danger rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-line">
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={total}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Eliminar documento"
        description={`Se eliminará «${pendingDelete?.filename ?? ""}» y sus chunks indexados. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Eliminar documentos"
        description={`Se eliminarán ${selected.size} documento(s) y sus chunks indexados. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        variant="destructive"
        loading={bulkBusy}
        onConfirm={() => {
          bulkDelete([...selected]);
          setBulkDeleteOpen(false);
        }}
      />

      <DocumentTextViewer
        isOpen={viewerDoc !== null}
        onClose={() => setViewerDoc(null)}
        documentName={viewerDoc?.filename ?? ""}
        text={detailQuery.data?.texto_extraido}
        loading={detailQuery.isLoading}
      />
    </section>
  );
}
